// Предпросмотр письма покупателю для «Настройки → Статусы»: показывает готовый текст с
// подставленными значениями, чтобы сотруднику не приходилось читать подстановки в фигурных
// скобках. Повторяет серверную сборку (backend/app/order_statuses.py: render_template,
// compose_completed) — держать в согласии с ней.

/** Подстановки, которые сотрудник вставляет кнопками. `insert` — что уходит в текст.
 *  Ссылок на отзывы здесь намеренно нет: в письме они идут инлайн-кнопками под сообщением,
 *  а не текстом (решение юзера 23.09.2026). Подстановки `{review_links}` / `{review_2gis}` /
 *  `{review_yandex}` сервер по-прежнему понимает — если кто-то впишет их руками. */
export const PLACEHOLDER_CHIPS = [
  { insert: '{order_number}', title: 'Номер заказа' },
  { insert: '{brand}', title: 'Название магазина' },
  { insert: '{review_invite}', title: 'Приглашение к отзыву', only: 'completed' },
];

const LINK_TITLES = [['gis', '2GIS'], ['yandex', 'Яндекс Карты']];

/** Инлайн-кнопки, которые бот прикрепит под письмом «Завершён» (bot/main.py handle_notify_order) */
export const REVIEW_BUTTONS = [
  { key: 'gis', title: '⭐ Отзыв в 2GIS' },
  { key: 'yandex', title: '⭐ Отзыв в Яндекс' },
];

/** Какие кнопки реально уйдут при таких настройках ссылок */
export function reviewButtonsFor(reviewLinks) {
  const links = reviewLinks || {};
  return REVIEW_BUTTONS.filter((b) => links[b.key]);
}

/**
 * Куда встанет подстановка при нажатии кнопки.
 *
 * `caret === null` — в поле не ставили курсор (кнопку нажали, не кликнув в текст): тогда
 * дописываем В КОНЕЦ, а не в начало. Иначе подстановка влезала перед первым словом
 * («{order_number}Спасибо за заказ!») — замечание юзера 23.09.2026.
 * Разделитель добавляем сами, чтобы подстановка не слипалась с соседним словом.
 */
export function insertToken(text, token, caret = null) {
  const src = String(text || '');
  const at = (typeof caret === 'number' && caret >= 0 && caret <= src.length) ? caret : src.length;
  const to = at;
  const before = src.slice(0, at);
  const after = src.slice(to);
  const needSpaceBefore = before.length > 0 && !/\s$/.test(before);
  const needSpaceAfter = after.length > 0 && !/^\s/.test(after);
  const value = (needSpaceBefore ? ' ' : '') + token + (needSpaceAfter ? ' ' : '');
  return { value: before + value + after, caret: before.length + value.length - (needSpaceAfter ? 1 : 0) };
}

/** Подстановка значений без str.format — фигурные скобки в тексте не ломают предпросмотр */
export function renderTemplate(template, { orderNumber = 1234, brand = '', reviewLinks = {} } = {}) {
  const links = reviewLinks || {};
  const block = LINK_TITLES
    .filter(([key]) => links[key])
    .map(([key, title]) => `${title}: ${links[key]}`)
    .join('\n');
  return String(template || '')
    .split('{order_number}').join(String(orderNumber))
    .split('{brand}').join(brand)
    .split('{review_2gis}').join(links.gis || '')
    .split('{review_yandex}').join(links.yandex || '')
    .split('{review_links}').join(block);
}

const collapse = (text) => {
  let out = text;
  while (out.indexOf('\n\n\n') >= 0) out = out.split('\n\n\n').join('\n\n');
  return out.trim();
};

/**
 * Текст, который увидит покупатель.
 * Для «Завершён» приглашение к отзыву встаёт на место {review_invite}, но только если ссылки
 * на отзывы заданы; без ссылок подстановка исчезает вместе с лишними пустыми строками.
 */
export function previewMessage(event, messages, { orderNumber = 1234, brand = '', reviewLinks = {} } = {}) {
  const opts = { orderNumber, brand, reviewLinks };
  const hasLinks = Object.values(reviewLinks || {}).some(Boolean);
  const text = renderTemplate((messages || {})[event], opts);
  if (event !== 'completed') return collapse(text);
  const invite = hasLinks ? renderTemplate((messages || {}).review_invite, opts) : '';
  if (text.indexOf('{review_invite}') >= 0) return collapse(text.split('{review_invite}').join(invite));
  return collapse(invite ? `${text}\n\n${invite}` : text);
}
